/**
 * 重大资产**回报面板**（`AC-R7.2` / `R7.3` / `R7.4` / `R8.4` · 端侧渲染层）
 *
 * 依据：`AC-R7.2`（四数由服务端算好）· `AC-R7.3`（分母 = 全额成本）·
 * `AC-R7.4`（未估值不显示回报率）· `AC-R8.4`（已处置显示最终回报）·
 * `AC-R3.4`（净权益**仅提示**）· `AC-R2.3`（账面净值不进净资产）·
 * 原型 `16-重大资产_原型.html:276–317` · 铁律 8 · 31 · 95。
 *
 * ## 🔴 本组件**不做任何算术**
 *
 * 四格里的每个数都直接读 `view.returnView.*`（服务端 `composeMajorAssetReturn` 的输出）。
 * 端侧自算回报率 = 违规（`AC-R7.2` 点名对象，架构断言 `client-no-money-math` 覆盖）。
 *
 * ## 🔴 三态在**渲染层**就分开（不是靠 `? :` 藏在数字里）
 *
 * | `state` | 第三格 | 第四格 |
 * |---|---|---|
 * | `holding` | 账面盈亏（估值 − 投入 + 收益） | 回报率 |
 * | `disposed` | **最终回报**（实得 − 投入 + 收益） | 「已处置」+ 实得 |
 * | `unvalued` | 「待估值」 | 「待估值」（**不显示回报率**，也不以 0 代入） |
 */

import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { formatMoneyText, type Cents, type MajorAssetDetailView } from '@jiaying/shared';
import { Amount } from './Amount.js';
import { percentWithSign, roiDirectionOf, returnStateTextOf } from '../features/major-asset.js';
import {
  BOOK_VALUE_HINT,
  DETAIL_RETURN_HINT,
  DISPOSED_STATE_TEXT,
  NET_EQUITY_HINT_PREFIX,
  NET_EQUITY_HINT_SUFFIX,
  PENDING_VALUATION_TEXT,
  RETURN_FINAL_PNL,
  RETURN_INCOME,
  RETURN_INVESTED,
  RETURN_PNL,
  RETURN_PROCEEDS,
  RETURN_ROI,
} from '../features/major-asset-labels.js';

export interface MajorAssetReturnPanelProps {
  view: MajorAssetDetailView;
}

/** 一格 */
function Cell({
  label,
  children,
  hint,
  testId,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  testId: string;
}) {
  return (
    <Box data-testid={testId} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      {children}
      {hint !== undefined && (
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export function MajorAssetReturnPanel({ view }: MajorAssetReturnPanelProps) {
  const r = view.returnView;
  const state = r.state;
  const direction = roiDirectionOf(view);
  const roiColor =
    direction === 0 ? 'text.secondary' : direction > 0 ? 'success.main' : 'error.main';
  const base = view.currency;

  return (
    <Box data-testid="ma-return-panel">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          回报面板
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {DETAIL_RETURN_HINT}
        </Typography>
        <Chip
          size="small"
          label={returnStateTextOf(view)}
          color={state === 'disposed' ? 'default' : 'success'}
          variant={state === 'disposed' ? 'outlined' : 'filled'}
          data-testid="ma-return-state"
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' },
          gap: 2,
        }}
      >
        <Cell label={RETURN_INVESTED} testId="ma-return-invested">
          <Amount
            cents={r.investedCents as Cents}
            currency={base}
            colored={false}
            variant="h6"
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            全额成本 · 含贷款部分
          </Typography>
        </Cell>

        <Cell label={RETURN_INCOME} testId="ma-return-income">
          <Amount
            cents={r.incomeCents as Cents}
            currency={base}
            colored={r.incomeCents > 0}
            variant="h6"
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            租金 / 投资收益 · 计收入
          </Typography>
        </Cell>

        {/* 第三格：按 state 三态分开 */}
        {state === 'disposed' ? (
          <Cell label={RETURN_FINAL_PNL} testId="ma-return-final-pnl">
            <Amount
              cents={(r.finalPnlCents ?? 0) as Cents}
              currency={base}
              variant="h6"
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="caption" color="text.secondary" component="div">
              实得 − 累计投入 + 累计收益
            </Typography>
          </Cell>
        ) : state === 'unvalued' ? (
          <Cell label={RETURN_PNL} testId="ma-return-pnl-pending">
            <Typography variant="h6" color="text.secondary">
              {PENDING_VALUATION_TEXT}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              未填估值 ⇒ 不算盈亏（不以 0 代入）
            </Typography>
          </Cell>
        ) : (
          <Cell label={RETURN_PNL} testId="ma-return-pnl">
            <Amount
              cents={(r.pnlCents ?? 0) as Cents}
              currency={base}
              variant="h6"
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="caption" color="text.secondary" component="div">
              估值 − 投入 + 收益
            </Typography>
          </Cell>
        )}

        {/* 第四格：回报率 / 已处置 / 待估值 */}
        {state === 'disposed' ? (
          <Cell label={RETURN_PROCEEDS} testId="ma-return-proceeds">
            <Amount
              cents={(r.disposedCents ?? 0) as Cents}
              currency={base}
              colored={false}
              variant="h6"
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="caption" color="text.secondary" component="div">
              {DISPOSED_STATE_TEXT} · 最终回报已收口
            </Typography>
          </Cell>
        ) : (
          <Cell label={RETURN_ROI} testId="ma-return-roi">
            {state === 'unvalued' || r.roiE4 === null ? (
              <Typography variant="h6" color="text.secondary" data-testid="ma-return-roi-none">
                {PENDING_VALUATION_TEXT}
              </Typography>
            ) : (
              <Typography
                variant="h6"
                sx={{ color: roiColor, fontWeight: 600 }}
                data-testid="ma-return-roi-value"
              >
                {percentWithSign(r.roiE4)}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" component="div">
              账面盈亏 ÷ 累计投入
            </Typography>
          </Cell>
        )}
      </Box>

      {/* 净权益提示（`AC-R3.4`）：有贷款、有估值、且**未处置** —— 卖掉了就没什么可提示 */}
      {view.disposedDate === null && view.loans.length > 0 && view.netEquityCents !== null && (
        <Box
          data-testid="ma-net-equity"
          sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}
        >
          <Typography variant="body2">
            {NET_EQUITY_HINT_PREFIX}
            {formatMoneyText(view.netEquityCents as Cents, base)}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {NET_EQUITY_HINT_SUFFIX}
          </Typography>
        </Box>
      )}

      {/* 账面净值（`AC-R2.3`）：只展示，不进净资产、不进回报 */}
      {view.bookValueCents !== null && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" rowGap={0.5}>
            <Typography variant="body2" color="text.secondary">
              账面净值
            </Typography>
            <Amount
              cents={view.bookValueCents as Cents}
              currency={base}
              colored={false}
              variant="body2"
              sx={{ fontWeight: 600 }}
              data-testid="ma-book-value"
            />
            <Typography variant="caption" color="text.secondary">
              {BOOK_VALUE_HINT}
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}
